import { ReportCategory, RiskLevel } from '../types';

// ===== Marca Previx =====
export const BRAND = {
  primary: '#0F4C81',
  primaryLight: '#1976A6',
  accent: '#F59E0B',
  ink: '#0f172a',
  slate: '#475569',
  bg: '#f4f7fb',
};

// ===== Categorías de reporte =====
export const CATEGORY_META: Record<ReportCategory, { label: string; short: string; color: string; icon: string; help: string }> = {
  ACTO_SUBESTANDAR: {
    label: 'Acto subestándar',
    short: 'Acto',
    color: '#dc2626',
    icon: 'person',
    help: 'Una persona actuando de forma insegura',
  },
  CONDICION_SUBESTANDAR: {
    label: 'Condición subestándar',
    short: 'Condición',
    color: '#f97316',
    icon: 'wrench',
    help: 'Algo del entorno o equipo está inseguro',
  },
  CASI_ACCIDENTE: {
    label: 'Casi accidente',
    short: 'Casi accidente',
    color: '#eab308',
    icon: 'bolt',
    help: 'Estuvo a punto de pasar, pero no pasó',
  },
  INCIDENTE: {
    label: 'Incidente',
    short: 'Incidente',
    color: '#7c3aed',
    icon: 'alert',
    help: 'Ya ocurrió un evento no deseado',
  },
};

export const CATEGORY_ORDER: ReportCategory[] = [
  'ACTO_SUBESTANDAR',
  'CONDICION_SUBESTANDAR',
  'CASI_ACCIDENTE',
  'INCIDENTE',
];

export function categoryLabel(cat: string): string {
  return (CATEGORY_META as any)[cat]?.label || cat;
}
export function categoryColor(cat: string): string {
  return (CATEGORY_META as any)[cat]?.color || '#64748b';
}

// ===== Niveles de riesgo IPERC =====
export const RISK_META: Record<RiskLevel, { label: string; color: string; text: string }> = {
  TRIVIAL: { label: 'Trivial', color: '#16a34a', text: '#fff' },
  TOLERABLE: { label: 'Tolerable', color: '#84cc16', text: '#1a2e05' },
  MODERADO: { label: 'Moderado', color: '#eab308', text: '#422006' },
  IMPORTANTE: { label: 'Importante', color: '#f97316', text: '#fff' },
  INTOLERABLE: { label: 'Intolerable', color: '#dc2626', text: '#fff' },
};

export const RISK_ORDER: RiskLevel[] = ['TRIVIAL', 'TOLERABLE', 'MODERADO', 'IMPORTANTE', 'INTOLERABLE'];

export function riskColor(level?: string): string {
  return (RISK_META as any)[level || '']?.color || '#94a3b8';
}
export function riskLabel(level?: string): string {
  return (RISK_META as any)[level || '']?.label || (level || '—');
}

// Previsualización cliente (misma fórmula que el backend):
// probabilidad = suma de los 4 índices (4-12); valor = prob × severidad; bandas por valor.
export function classifyRisk(
  idxPeople: number,
  idxProcedures: number,
  idxTraining: number,
  idxExposure: number,
  severityIndex: number
): { probability: number; riskValue: number; riskLevel: RiskLevel } {
  const probability = idxPeople + idxProcedures + idxTraining + idxExposure;
  const riskValue = probability * severityIndex;
  let riskLevel: RiskLevel;
  if (riskValue <= 4) riskLevel = 'TRIVIAL';
  else if (riskValue <= 8) riskLevel = 'TOLERABLE';
  else if (riskValue <= 16) riskLevel = 'MODERADO';
  else if (riskValue <= 24) riskLevel = 'IMPORTANTE';
  else riskLevel = 'INTOLERABLE';
  return { probability, riskValue, riskLevel };
}

// ===== Estados (badges genéricos con fallback) =====
const STATUS_COLORS: Record<string, string> = {
  // reportes
  NUEVO: '#3b82f6',
  RECIBIDO: '#3b82f6',
  EN_REVISION: '#f59e0b',
  EN_PROCESO: '#f59e0b',
  EN_ACCION: '#7c3aed',
  ATENDIDO: '#16a34a',
  CERRADO: '#16a34a',
  ESCALADO: '#7c3aed',
  DESCARTADO: '#94a3b8',
  // incidentes
  REPORTADO: '#3b82f6',
  EN_INVESTIGACION: '#f59e0b',
  // acciones
  PENDIENTE: '#3b82f6',
  COMPLETADA: '#16a34a',
  VENCIDA: '#dc2626',
  // inspecciones
  ABIERTA: '#f59e0b',
  CERRADA: '#16a34a',
  // checklist / monitoreo
  CONFORME: '#16a34a',
  NO_CONFORME: '#dc2626',
  NA: '#94a3b8',
  // aptitud EMO
  APTO: '#16a34a',
  APTO_CON_RESTRICCIONES: '#f59e0b',
  NO_APTO: '#dc2626',
  OBSERVADO: '#f59e0b',
  // auditorías
  PLANIFICADA: '#3b82f6',
  // documentos del SG-SST / plan anual
  BORRADOR: '#64748b',
  VIGENTE: '#16a34a',
  OBSOLETO: '#94a3b8',
  // hallazgos de auditoría
  NO_CONFORMIDAD: '#dc2626',
  OBSERVACION: '#f59e0b',
  OPORTUNIDAD: '#1976A6',
};

export function statusColor(status?: string): string {
  if (!status) return '#94a3b8';
  return STATUS_COLORS[status] || '#64748b';
}

export function humanize(s?: string): string {
  if (!s) return '—';
  return s
    .toLowerCase()
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

export function formatDate(iso?: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('es-PE', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

export function formatDateTime(iso?: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleString('es-PE', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' });
}

export function isOverdue(dueDate?: string | null, status?: string): boolean {
  if (!dueDate) return false;
  if (status === 'COMPLETADA') return false;
  const d = new Date(dueDate);
  if (isNaN(d.getTime())) return false;
  return d.getTime() < Date.now();
}

// ===== Vencimiento EMO / fechas =====
// 'vencido'  → valid_until < hoy
// 'porvencer'→ valid_until dentro de los próximos `days` días (por defecto 30)
// 'vigente'  → aún válido
// null       → sin fecha de vencimiento
export type ExpiryState = 'vencido' | 'porvencer' | 'vigente' | null;

export function expiryState(validUntil?: string | null, days = 30): ExpiryState {
  if (!validUntil) return null;
  const d = new Date(validUntil);
  if (isNaN(d.getTime())) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const limit = new Date(today);
  limit.setDate(limit.getDate() + days);
  if (d < today) return 'vencido';
  if (d <= limit) return 'porvencer';
  return 'vigente';
}

// Color de resaltado por estado de vencimiento (vencido rojo, por vencer ámbar).
export function expiryColor(state: ExpiryState): string {
  if (state === 'vencido') return '#dc2626';
  if (state === 'porvencer') return '#f59e0b';
  return '#16a34a';
}

export function expiryLabel(state: ExpiryState): string {
  if (state === 'vencido') return 'Vencido';
  if (state === 'porvencer') return 'Por vencer';
  if (state === 'vigente') return 'Vigente';
  return '—';
}

export const INCIDENT_TYPES = [
  'PRIMEROS_AUXILIOS',
  'TRABAJO_RESTRINGIDO',
  'TIEMPO_PERDIDO',
  'INCAPACITANTE',
  'FATAL',
  'DANO_MATERIAL',
  'AMBIENTAL',
  'OTRO',
];
